 
import * as Yup from 'yup';
import { ProFormaModelIds } from 'constants/proForma';
import { DEVELOPMENT_SOURCE_METRIC } from 'constants/sources';
import { BOESlideoutFormValues } from './types';

export enum COST_BASIS_TYPES {
    PER_SF = 'sf',
    PER_UNIT = 'unit',
    TOTAL = 'total',
    PER_KW = 'kw',
    PER_MW = 'mw',
}

export const COST_BASIS_OPTIONS = [
    {
        label: 'per unit',
        value: COST_BASIS_TYPES.PER_UNIT,
    },
    {
        label: 'per SF',
        value: COST_BASIS_TYPES.PER_SF,
    },
];

export const DATA_CENTER_COST_BASIS_OPTIONS = [
    {
        label: 'per kW',
        value: COST_BASIS_TYPES.PER_KW,
    },
    {
        label: 'per MW',
        value: COST_BASIS_TYPES.PER_MW,
    },
];

export const HARD_SOFT_COSTS_OPTIONS = [
    {
        label: 'total',
        value: COST_BASIS_TYPES.TOTAL,
    },
    ...COST_BASIS_OPTIONS,
];

export const DATA_CENTER_HARD_SOFT_COSTS_OPTIONS = [
    {
        label: 'total',
        value: COST_BASIS_TYPES.TOTAL,
    },
    ...DATA_CENTER_COST_BASIS_OPTIONS,
];

export enum UNIT_OF_TIME_TYPES {
    YEARLY = 'yearly',
    MONTHLY = 'monthly',
}

export enum UNIT_OF_TIME_TYPES_UI {
    ANNUAL = 'Annual',
    MONTHLY = 'Monthly',
}

export const UNIT_OF_TIME_OPTIONS = [
    {
        label: UNIT_OF_TIME_TYPES_UI.MONTHLY,
        value: UNIT_OF_TIME_TYPES.MONTHLY,
    },
    {
        // Backend = Yearly ; Frontend = Annual
        label: UNIT_OF_TIME_TYPES_UI.ANNUAL,
        value: UNIT_OF_TIME_TYPES.YEARLY,
    },
];

export enum EXPENSE_CALCULATION_MODE {
    UNIT_COST = 'UNIT_COST',
    PERCENT_OF_GPR = 'PERCENT_OF_GPR',
}

export const EXPENSE_CALCULATION_MODE_OPTIONS = [
    {
        label: 'Unit cost',
        value: EXPENSE_CALCULATION_MODE.UNIT_COST,
    },
    {
        label: '% of GPR',
        value: EXPENSE_CALCULATION_MODE.PERCENT_OF_GPR,
    },
];

export const LOAN_CALCULATION_OPTIONS = [
    {
        label: 'LTV',
        value: DEVELOPMENT_SOURCE_METRIC.LTV,
    },
    {
        label: 'LTC',
        value: DEVELOPMENT_SOURCE_METRIC.LTC,
    },
    {
        label: 'Debt yield',
        value: DEVELOPMENT_SOURCE_METRIC.DEBT_YIELD,
    },
];

export const validationSchema = Yup.object().shape({
    grossSquareFootage: Yup.number()
        .min(0, 'Invalid value')
        .required('Required'),
    efficiency: Yup.number()
        .min(0, 'Invalid value')
        .max(100, 'Invalid value')
        .required('Required'),
    rentPrice: Yup.number()
        .min(0, 'Invalid value')
        .required('Required'),
    units: Yup.number()
        .min(0, 'Invalid value')
        .required('Required'),
    expenses: Yup.number()
        .when('expenseCalculationMode', {
            is: EXPENSE_CALCULATION_MODE.UNIT_COST,
            then: schema => schema.min(0, 'Invalid value').required('Required'),
            otherwise: schema => schema.nullable(),
        }),
    expensePercentOfGpr: Yup.number()
        .when('expenseCalculationMode', {
            is: EXPENSE_CALCULATION_MODE.PERCENT_OF_GPR,
            then: schema => schema.min(0, 'Invalid value').max(100, 'Cannot exceed 100%').required('Required'),
            otherwise: schema => schema.nullable(),
        }),
    exitCapRate: Yup.number()
        .min(0, 'Invalid value')
        .required('Required'),
    acquisitionCosts: Yup.number()
        .min(0, 'Invalid value')
        .required('Required'),
    hardCosts: Yup.number()
        .min(0, 'Invalid value')
        .required('Required'),
    softCosts: Yup.number()
        .min(0, 'Invalid value')
        .required('Required'),
    developerFee: Yup.number()
        .min(0, 'Invalid value')
        .required('Required'),
    apr: Yup.number()
        .min(0, 'Invalid value')
        .required('Required'),
    avgLoanBalance: Yup.number()
        .min(0, 'Invalid value')
        .required('Required'),
    loanTerm: Yup.number()
        .min(0, 'Invalid value')
        .required('Required'),
    contructionFinancing: Yup.number()
        .min(0, 'Invalid value')
        .required('Required'),
    marketCapRate: Yup.number()
        .nullable()
        .min(0, 'Invalid value'),
    retailGrossSquareFootage: Yup.number()
        .nullable()
        .min(0, 'Invalid value')
        .when('propertyType', {
            is: (pt: any) => Number(pt?.value) === ProFormaModelIds.APARTMENT_WITH_RETAIL,
            then: schema => schema.required('Required'),
        }),
    retailNumberOfSuites: Yup.number()
        .nullable()
        .min(1, 'Invalid value')
        .integer('Must be a whole number')
        .when('propertyType', {
            is: (pt: any) => Number(pt?.value) === ProFormaModelIds.APARTMENT_WITH_RETAIL,
            then: schema => schema.required('Required'),
        }),
    retailRentPrice: Yup.number()
        .nullable()
        .min(0, 'Invalid value')
        .when('propertyType', {
            is: (pt: any) => Number(pt?.value) === ProFormaModelIds.APARTMENT_WITH_RETAIL,
            then: schema => schema.required('Required'),
        }),
    retailExpenses: Yup.number()
        .nullable()
        .min(0, 'Invalid value')
        .when(['propertyType', 'retailExpenseCalculationMode'], {
            is: (pt: any, mode: any) =>
                Number(pt?.value) === ProFormaModelIds.APARTMENT_WITH_RETAIL &&
                mode === EXPENSE_CALCULATION_MODE.UNIT_COST,
            then: schema => schema.required('Required'),
            otherwise: schema => schema.nullable(),
        }),
    retailExpensePercentOfGpr: Yup.number()
        .nullable()
        .min(0, 'Invalid value')
        .when(['propertyType', 'retailExpenseCalculationMode'], {
            is: (pt: any, mode: any) =>
                Number(pt?.value) === ProFormaModelIds.APARTMENT_WITH_RETAIL &&
                mode === EXPENSE_CALCULATION_MODE.PERCENT_OF_GPR,
            then: schema => schema.required('Required'),
            otherwise: schema => schema.nullable(),
        }),
});

export const BOEDefaultValuesByPropertyType: Record<string, BOESlideoutFormValues> = {
    apartment: {
        id: null,
        dealId: null,
        name: '',
        propertyType: { label: 'Apartment', value: 'apartment' },
        selectedLoanCalculationOptions: LOAN_CALCULATION_OPTIONS[1],
        grossSquareFootage: 120000,
        efficiency: 85,
        rentPrice: 1900,
        units: 150,
        expenses: 7.5,
        expenseCalculationMode: EXPENSE_CALCULATION_MODE.UNIT_COST,
        expensePercentOfGpr: null,
        exitCapRate: 5.5,
        acquisitionCosts: 4800000,
        hardCosts: 210,
        softCosts: 50,
        developerFee: 5,
        apr: 8,
        avgLoanBalance: 60,
        loanTerm: 3,
        contructionFinancing: 65,
        marketCapRate: 5,
        developerFeeTotal: 2101821.58,
        interestReserveTotal: 3934610,
        totalDevelopmentCosts: 42036431.57,
        loanAmount: 27323680.52,
        equityCost: 14712751.05,
        rentPriceSubdiv: 'unit',
        expenseCostSubdiv: 'sf',
        softCostsSubdiv: 'sf',
        hardCostsSubdiv: 'sf',
        rentPriceTime: 'monthly',
        expenseCostTime: 'yearly',
        loanTermTime: 'yearly',
        isNewScenario: false,
        createNewDeal: false,
    },
    data_center: {
        id: null,
        dealId: null,
        name: '',
        propertyType: { label: 'Data Center', value: 'data_center' },
        selectedLoanCalculationOptions: LOAN_CALCULATION_OPTIONS[1],
        grossSquareFootage: 80000,
        efficiency: 60,
        rentPrice: 2000,
        units: 4000,
        expenses: 350,
        expenseCalculationMode: EXPENSE_CALCULATION_MODE.UNIT_COST,
        expensePercentOfGpr: null,
        exitCapRate: 8,
        acquisitionCosts: 8000000,
        hardCosts: 8500,
        softCosts: 3500,
        developerFee: 4,
        apr: 8,
        avgLoanBalance: 65,
        loanTerm: 4,
        contructionFinancing: 60,
        marketCapRate: 5,
        developerFeeTotal: 2681992.34,
        interestReserveTotal: 8367816.09,
        totalDevelopmentCosts: 67049808.43,
        loanAmount: 40229885.06,
        equityCost: 26819923.37,
        rentPriceSubdiv: 'kw',
        expenseCostSubdiv: 'kw',
        softCostsSubdiv: 'kw',
        hardCostsSubdiv: 'kw',
        rentPriceTime: 'yearly',
        expenseCostTime: 'yearly',
        loanTermTime: 'yearly',
        isNewScenario: false,
        createNewDeal: false,
    },
    retail: {
        id: null,
        dealId: null,
        name: '',
        propertyType: { label: 'Retail', value: 'retail' },
        selectedLoanCalculationOptions: LOAN_CALCULATION_OPTIONS[1],
        grossSquareFootage: 25000,
        efficiency: 100,
        rentPrice: 32,
        units: 10,
        expenses: 7.5,
        expenseCalculationMode: EXPENSE_CALCULATION_MODE.UNIT_COST,
        expensePercentOfGpr: null,
        exitCapRate: 6.75,
        acquisitionCosts: 1000000,
        hardCosts: 190,
        softCosts: 42,
        developerFee: 4,
        apr: 7.25,
        avgLoanBalance: 60,
        loanTerm: 30,
        contructionFinancing: 70,
        marketCapRate: 5,
        developerFeeTotal: 307735.82,
        interestReserveTotal: 585659.74,
        totalDevelopmentCosts: 7693395.56,
        loanAmount: 5385376.89,
        equityCost: 2308018.67,
        rentPriceSubdiv: 'sf',
        expenseCostSubdiv: 'sf',
        softCostsSubdiv: 'sf',
        hardCostsSubdiv: 'sf',
        rentPriceTime: 'yearly',
        expenseCostTime: 'yearly',
        loanTermTime: 'monthly',
        isNewScenario: false,
        createNewDeal: false,
    },
    office: {
        id: null,
        dealId: null,
        name: '',
        propertyType: { label: 'Office', value: 'office' },
        selectedLoanCalculationOptions: LOAN_CALCULATION_OPTIONS[1],
        grossSquareFootage: 90000,
        efficiency: 90,
        rentPrice: 39,
        units: 10,
        expenses: 8.5,
        expenseCalculationMode: EXPENSE_CALCULATION_MODE.UNIT_COST,
        expensePercentOfGpr: null,
        exitCapRate: 6,
        acquisitionCosts: 4000000,
        hardCosts: 260,
        softCosts: 55,
        developerFee: 4,
        apr: 7.25,
        avgLoanBalance: 60,
        loanTerm: 30,
        contructionFinancing: 72,
        marketCapRate: 5,
        developerFeeTotal: 1467619.37,
        interestReserveTotal: 2872864.92,
        totalDevelopmentCosts: 36690484.29,
        loanAmount: 26417148.69,
        equityCost: 10273335.6,
        rentPriceSubdiv: 'sf',
        expenseCostSubdiv: 'sf',
        softCostsSubdiv: 'sf',
        hardCostsSubdiv: 'sf',
        rentPriceTime: 'yearly',
        expenseCostTime: 'yearly',
        loanTermTime: 'monthly',
        isNewScenario: false,
        createNewDeal: false,
    },
    industrial: {
        id: null,
        dealId: null,
        name: '',
        propertyType: { label: 'Industrial', value: 'industrial' },
        selectedLoanCalculationOptions: LOAN_CALCULATION_OPTIONS[1],
        grossSquareFootage: 150000,
        efficiency: 100,
        rentPrice: 12,
        units: 4,
        expenses: 2.5,
        expenseCalculationMode: EXPENSE_CALCULATION_MODE.UNIT_COST,
        expensePercentOfGpr: null,
        exitCapRate: 5.75,
        acquisitionCosts: 1800000,
        hardCosts: 90,
        softCosts: 17,
        developerFee: 3.5,
        apr: 7,
        avgLoanBalance: 60,
        loanTerm: 24,
        contructionFinancing: 60,
        marketCapRate: 5,
        developerFeeTotal: 683085.5,
        interestReserveTotal: 983643.12,
        totalDevelopmentCosts: 19516728.62,
        loanAmount: 11710037.17,
        equityCost: 7806691.45,
        rentPriceSubdiv: 'sf',
        expenseCostSubdiv: 'sf',
        softCostsSubdiv: 'sf',
        hardCostsSubdiv: 'sf',
        rentPriceTime: 'yearly',
        expenseCostTime: 'yearly',
        loanTermTime: 'monthly',
        isNewScenario: false,
        createNewDeal: false,
    },
};

// Also used in API to map to asset type. update both if you change this enum
export enum BOE_PROPERTY_TYPES {
    APARTMENT = 'apartment',
    DATA_CENTER = 'data_center',
    RETAIL = 'retail',
    OFFICE = 'office',
    INDUSTRIAL = 'industrial',
    APARTMENT_WITH_RETAIL = 'apartment_with_retail',
}

export const PropertyTypeToIdMap: Record<string, ProFormaModelIds> = {
    [BOE_PROPERTY_TYPES.APARTMENT]: ProFormaModelIds.APARTMENT,
    [BOE_PROPERTY_TYPES.DATA_CENTER]: ProFormaModelIds.DATA_CENTER,
    [BOE_PROPERTY_TYPES.RETAIL]: ProFormaModelIds.RETAIL,
    [BOE_PROPERTY_TYPES.OFFICE]: ProFormaModelIds.OFFICE,
    [BOE_PROPERTY_TYPES.INDUSTRIAL]: ProFormaModelIds.INDUSTRIAL,
    [BOE_PROPERTY_TYPES.APARTMENT_WITH_RETAIL]: ProFormaModelIds.APARTMENT_WITH_RETAIL,
};

export const defaultFinancingByMetric: Record<string, number> = {
    [DEVELOPMENT_SOURCE_METRIC.LTV]: 55,
    [DEVELOPMENT_SOURCE_METRIC.LTC]: 60,
    [DEVELOPMENT_SOURCE_METRIC.DEBT_YIELD]: 9,
};

export const PropertyTypeIdUnitsMap: Record<number, string> = {
    [ProFormaModelIds.APARTMENT]: 'Unit',
    [ProFormaModelIds.APARTMENT_WITH_RETAIL]: 'Unit',
    [ProFormaModelIds.DATA_CENTER]: 'Kilowatt',
    [ProFormaModelIds.RETAIL]: 'Suite',
    [ProFormaModelIds.OFFICE]: 'Suite',
    [ProFormaModelIds.INDUSTRIAL]: 'Suite',
};
